import { fg } from '@atlaskit/platform-feature-flags';
import { requestWithAbort } from '@atlassian/twg-common';

export type CollabGraphRecommendedEntity = {
	id: string;
	score: number;
	expand: {
		ari: string;
		account_id: string;
		email: string;
		name: string;
		picture: string;
		title?: string;
		jobTitle?: string;
		roleTitle?: string;
		position?: string;
		extended_profile?: {
			job_title?: string;
			title?: string;
		};
		extendedProfile?: {
			jobTitle?: string;
			job_title?: string;
			title?: string;
		};
		account_status?: 'active' | 'inactive' | 'closed';
		// There may be extra things returned
		[key: string]: unknown;
	};
};

export type CollabGraphResponse = {
	recommendedEntities: CollabGraphRecommendedEntity[];
};

/**
 * Reasons a Collaboration Graph recommendation cannot be rendered at all.
 * These map to events that would crash the `CollaboratorsSection` renderer
 * (e.g. `entity.expand.ari`) or that would produce a meaningless row with
 * no usable display label. Entities flagged with any of these reasons are
 * filtered out of `CollaborationUsersAtom`'s emitted `data`.
 */
export type RecommendationDropReason =
	| 'entity-null-or-non-object'
	| 'expand-missing'
	| 'name-missing';

/**
 * Reasons a recommendation is degraded but still renderable. The atom
 * keeps these entities in `data` so the user sees a row; we only emit
 * Sentry telemetry so we can pivot by the upstream `traceId` and chase
 * down why the Collaboration Graph response was incomplete.
 */
export type RecommendationWarning = 'ari-missing';

/**
 * Returns the first reason the entity must be dropped from the rendered
 * collaborator list, or `null` if it is renderable.
 */
export const getRecommendationDropReason = (
	entity: CollabGraphRecommendedEntity | null | undefined,
): RecommendationDropReason | null => {
	if (entity === null || entity === undefined || typeof entity !== 'object') {
		return 'entity-null-or-non-object';
	}

	const expand = entity.expand;
	if (expand === null || expand === undefined || typeof expand !== 'object') {
		return 'expand-missing';
	}

	const name = expand.name;
	if (typeof name !== 'string' || name.trim().length === 0) {
		return 'name-missing';
	}

	return null;
};

/**
 * Returns the warning reason for an otherwise-renderable entity, or
 * `null` if no warning applies. Only meaningful when
 * `getRecommendationDropReason(entity) === null` — callers should check
 * the drop reason first.
 */
export const getRecommendationWarning = (
	entity: CollabGraphRecommendedEntity,
): RecommendationWarning | null => {
	const ari = entity.expand.ari;
	if (typeof ari !== 'string' || ari.trim().length === 0) {
		return 'ari-missing';
	}
	return null;
};

export const isCollabGraphRecommendedEntity = (
	entity: CollabGraphRecommendedEntity | null | undefined,
): entity is CollabGraphRecommendedEntity => getRecommendationDropReason(entity) === null;

export type RequestCollaborationUsersInput = {
	requestingUserId: string;
	targetUserId: string;
	tenantId: string;
	limit: number;
	excludeInactive?: boolean;
};

/**
 * See docs: https://developer.atlassian.com/platform/collaboration-graph/rest/api-group-collaboration-graph-recommendations-api/#api-v2-recommend-user-post
 */
export const requestCollaborationUsers = ({
	requestingUserId,
	targetUserId,
	tenantId,
	limit,
	excludeInactive = false,
}: RequestCollaborationUsersInput): ReturnType<typeof requestWithAbort<CollabGraphResponse>> => {
	const body = {
		modelRequestParams: {
			experience: 'CgUserNearbyUser',
			caller: 'twg-microsite',
			featureServiceModel: 'cg_user_user_v5',
		},
		expand: 'all',
		requestingUserId,
		context: {
			userId: targetUserId,
			tenantId,
		},
		maxNumberOfResults: limit,
		...(fg('twg-microsite-hide-deactivate-collaborators') ? { excludeInactive } : {}),
	};

	return requestWithAbort<CollabGraphResponse>(
		'post',
		`/gateway/api/collaboration/v2/recommend/user`,
		body,
		{
			credentials: 'include',
		},
	);
};
